/**
 * Job Sorting Utility
 *
 * Client-side sorting for job list views.
 *
 * DDD Note: Sorting is a PRESENTATION concern when applied
 * to already-fetched data. Domain doesn't care about display order.
 */

import type { JobCardViewModel } from '@/application/service-provider/view-models/JobListViewModel';

export type SortOption =
  | 'date-desc'
  | 'date-asc'
  | 'name-asc'
  | 'name-desc'
  | 'status';

/**
 * Sort jobs client-side
 *
 * @param jobs - Array of job view models to sort
 * @param sortBy - Sort option to apply
 * @returns Sorted copy of jobs array (does not mutate original)
 */
export function sortJobs(
  jobs: JobCardViewModel[],
  sortBy: SortOption
): JobCardViewModel[] {
  const sorted = [...jobs]; // Don't mutate original

  switch (sortBy) {
    case 'date-desc':
      return sorted.sort((a, b) => {
        // Sort by start date timestamp
        return b.startDateTimestamp - a.startDateTimestamp;
      });

    case 'date-asc':
      return sorted.sort((a, b) => {
        return a.startDateTimestamp - b.startDateTimestamp;
      });

    case 'name-asc':
      return sorted.sort((a, b) =>
        a.displayName.localeCompare(b.displayName)
      );

    case 'name-desc':
      return sorted.sort((a, b) =>
        b.displayName.localeCompare(a.displayName)
      );

    case 'status': {
      // Sort by status priority: in_progress > scheduled > pending > completed > cancelled
      const statusOrder: Record<string, number> = {
        'in_progress': 0,
        'scheduled': 1,
        'pending': 2,
        'completed': 3,
        'cancelled': 4
      };
      return sorted.sort((a, b) => {
        const orderA = statusOrder[a.status] ?? 99;
        const orderB = statusOrder[b.status] ?? 99;
        return orderA - orderB;
      });
    }

    default:
      return sorted;
  }
}

/**
 * Get sort option label for UI display
 */
export function getSortLabel(sortBy: SortOption): string {
  const labels: Record<SortOption, string> = {
    'date-desc': 'Date (Newest First)',
    'date-asc': 'Date (Oldest First)',
    'name-asc': 'Name (A-Z)',
    'name-desc': 'Name (Z-A)',
    'status': 'Status'
  };
  return labels[sortBy];
}

/**
 * Get all available sort options
 */
export function getSortOptions(): Array<{ value: SortOption; label: string }> {
  return [
    { value: 'date-desc', label: 'Date (Newest First)' },
    { value: 'date-asc', label: 'Date (Oldest First)' },
    { value: 'name-asc', label: 'Name (A-Z)' },
    { value: 'name-desc', label: 'Name (Z-A)' },
    { value: 'status', label: 'Status' }
  ];
}
